/* eslint-disable no-console */
import { uuidv4 } from '@/utils/helpers';
import axios from 'axios';
import arobaPublicAxios from '../axios/aroba-oapi-axios.service';
import { dialogService } from '../dialog.service';
const baseDomain = process.env.VUE_APP_API_BASE_URL;

const options = {
  headers: {
    'Content-Type': 'application/json',
  },
};

const requestsController = new Map();
const registeredRequests = new Map();

/**
 * Http GET
 */
export async function get(
  url,
  params = {},
  autoHandleError = () => true,
  exceptHandleErrorStatus = []
) {
  const controller = new AbortController();
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      value.forEach((val) => {
        searchParams.append(key, val);
      });
    } else if (value !== null) {
      searchParams.append(key, value);
    }
  }
  const newOptions = {
    ...options,
    signal: controller.signal,
    params: searchParams,
  };

  if (params.__headers) {
    newOptions.headers = { ...newOptions.headers, ...params.__headers };
  }

  try {
    let requestURL = `${baseDomain}${url}`;
    if (url.startsWith('http')) {
      requestURL = `${url}`;
    }

    // set request controll if request id exist
    if (params.__reqId) {
      requestsController.set(params.__reqId, controller);
    }
    // call http request
    const result = await arobaPublicAxios.get(requestURL, newOptions);

    // delete request controller if request id exist
    if (params.__reqId) {
      requestsController.delete(params.__reqId);
    }
    return result.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      const obj = {};
      Object.setPrototypeOf(obj, { _isCancel: true });
      return obj;
    }
    throw errorWrapper(error, autoHandleError, exceptHandleErrorStatus);
  }
}

/**
 * Http POST
 */
export async function post(
  url,
  params = {},
  autoHandleError = () => true,
  exceptHandleErrorStatus = []
) {
  const controller = new AbortController();

  const newOptions = {
    signal: controller.signal,
    ...options,
  };

  try {
    let requestURL = `${baseDomain}${url}`;
    if (url.startsWith('http')) {
      requestURL = `${url}`;
    }

    // set request controll if request id exist
    if (params.__reqId) {
      requestsController.set(params.__reqId, controller);
    }

    if (params.__headers) {
      newOptions.headers = { ...newOptions.headers, ...params.__headers };
    }

    // call http request
    const result = await arobaPublicAxios.post(requestURL, params, newOptions);

    // delete request controller if request id exist
    if (params.__reqId) {
      requestsController.delete(params.__reqId);
    }

    return result.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      const obj = {};
      Object.setPrototypeOf(obj, { _isCancel: true });
      return obj;
    }
    throw errorWrapper(error, autoHandleError, exceptHandleErrorStatus);
  }
}

/**
 * Http PUT
 */
export async function put(
  url,
  params = {},
  autoHandleError = () => true,
  exceptHandleErrorStatus = []
) {
  const newOptions = {
    ...options,
  };

  try {
    let requestURL = `${baseDomain}${url}`;
    if (url.startsWith('http')) {
      requestURL = `${url}`;
    }
    const result = await arobaPublicAxios.put(requestURL, params, newOptions);
    return result.data;
  } catch (error) {
    throw errorWrapper(error, autoHandleError, exceptHandleErrorStatus);
  }
}

/**
 * Http DELETE
 */
export async function del(
  url,
  params = {},
  autoHandleError = () => true,
  exceptHandleErrorStatus = []
) {
  const newOptions = {
    ...options,
    data: params,
  };

  try {
    let requestURL = `${baseDomain}${url}`;
    if (url.startsWith('http')) {
      requestURL = `${url}`;
    }

    const result = await arobaPublicAxios.delete(requestURL, newOptions);
    return result.data;
  } catch (error) {
    throw errorWrapper(error, autoHandleError, exceptHandleErrorStatus);
  }
}

/**
 * Http Post by token id
 */
export function errorWrapper(
  error,
  autoHandleError = () => true,
  // eslint-disable-next-line no-unused-vars
  _exceptHandleErrorStatus = []
) {
  console.log('errorWrapper ', error);
  let errorData = {
    code: 'HttpService/WrongSetting',
    message: 'Error happened!',
  };
  let status = error.response ? error.response.status : 'common';

  if (error.response) {
    console.log('error.response, ', error.response);
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    console.log({ response: error.response });
    console.log(error.response.data);
    console.log(status);
    console.log(error.response.headers);
    errorData = { ...error.response.data, status };
  } else if (error.request) {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    console.log(error.request);

    errorData.code = 'HttpService/NoResponse';
    errorData.message = `
                The request was made but no response was received
                error.request is an instance of XMLHttpRequest in the browser and an instance of
                http.ClientRequest in node.js
                `;
  } else if (error.message === 'No internet connection') {
    status = 'NO_INTERNET';
    errorData.code = 'HttpService/NoInternet';
    errorData.message = error.message;
  } else {
    // Something happened in setting up the request that triggered an Error
    console.log('Error', error.message);
    errorData.code = 'HttpService/postWithoutAuth/WrongSetting';
    errorData.message = `
                Something happened in setting up the request that triggered an Error
                `;
  }
  if (autoHandleError(errorData)) {
    console.log(error.headers, error);
    dialogService.httpError({
      status,
      error: errorData,
      request: error.config,
      response: error.response,
    });
  }
  console.log(error.config);
  errorData._request = error.request;
  errorData._response = error.response;
  return errorData;
}

/**
 * Abort http request
 * @param {string} reqId
 */
export const abortRequest = (reqId) => {
  if (requestsController.has(reqId)) {
    const controller = requestsController.get(reqId);
    controller.abort();
    requestsController.delete(reqId);
  }
};

/**
 * Register http request using API name
 * @param {string} apiName
 * @param {*} payload
 */
export const registerHttpRequest = (apiName, payload, duplicate) => {
  const reqId = uuidv4();
  // prevent duplicate request
  if (registeredRequests.has(apiName) && !duplicate) {
    const oldReqId = registeredRequests.get(apiName);
    abortRequest(oldReqId);
  }
  registeredRequests.set(apiName, reqId);
  Object.setPrototypeOf(payload, { __reqId: reqId });
  return {
    reqId,
  };
};
